/* eslint-disable sonarjs/no-identical-functions */
import { NewPasswordNeededError } from "@domain/authentication/authentication-errors";
import { NetworkActionsErrorsManager } from "@domain/network/network-actions-errors-manager";
import { isNotNil } from "@utils/assert";
import {
	AuthenticationDetails,
	CognitoUser,
	CognitoUserAttribute,
	CognitoUserPool,
	CognitoUserSession,
	ISignUpResult,
} from "amazon-cognito-identity-js";

type CognitoGetSessionCallbackParameters = Parameters<Parameters<CognitoUser["getSession"]>[0]>;

export class CognitoServiceWrapper {
	constructor(private readonly networkActionsErrorsManager: NetworkActionsErrorsManager) {}

	getSession(user: CognitoUser): Promise<CognitoUserSession> {
		this.networkActionsErrorsManager.clearError();
		return new Promise((resolve, reject) => {
			user.getSession((...[error, session]: CognitoGetSessionCallbackParameters) => {
				if (session) {
					resolve(session);
				} else {
					reject(error);
				}
			});
		});
	}

	authenticateUser(user: CognitoUser, authenticationDetails: AuthenticationDetails): Promise<CognitoUserSession> {
		this.networkActionsErrorsManager.clearError();
		return new Promise((resolve, reject) => {
			user.authenticateUser(authenticationDetails, {
				newPasswordRequired: async () => {
					reject(new NewPasswordNeededError("The user needs to change their password"));
				},
				onSuccess: result => {
					resolve(result);
				},
				onFailure: error => {
					reject(error);
				},
			});
		});
	}

	forgotPassword(user: CognitoUser): Promise<void> {
		this.networkActionsErrorsManager.clearError();
		return new Promise((resolve, reject) => {
			user.forgotPassword({
				onSuccess: () => {
					resolve();
				},
				onFailure: error => {
					reject(error);
				},
			});
		});
	}

	confirmPassword(user: CognitoUser, resetToken: string, newPassword: string): Promise<void> {
		this.networkActionsErrorsManager.clearError();
		return new Promise((resolve, reject) => {
			user.confirmPassword(resetToken, newPassword, {
				onSuccess: () => {
					resolve();
				},
				onFailure: error => {
					reject(error);
				},
			});
		});
	}

	getUserAttributes(user: CognitoUser): Promise<CognitoUserAttribute[]> {
		// No clear error here, as there were none before refactor
		return new Promise((resolve, reject) => {
			user.getUserAttributes((error, result) => {
				if (!error && result) {
					resolve(result);
				} else {
					reject(error);
				}
			});
		});
	}

	updateAttributes(user: CognitoUser, attributes: CognitoUserAttribute[]): Promise<void> {
		this.networkActionsErrorsManager.clearError();
		return new Promise((resolve, reject) => {
			user.updateAttributes(attributes, async error => {
				if (isNotNil(error)) {
					reject(error);
				} else {
					resolve();
				}
			});
		});
	}

	signUp(
		userPool: CognitoUserPool,
		email: string,
		password: string,
		attributes: CognitoUserAttribute[] = [],
	): Promise<ISignUpResult> {
		this.networkActionsErrorsManager.clearError();
		return new Promise((resolve, reject) => {
			userPool.signUp(email, password, attributes, [], async (error, result) => {
				if (result && !error) {
					resolve(result);
				} else {
					reject(error);
				}
			});
		});
	}

	changePassword(user: CognitoUser, currentPassword: string, newPassword: string): Promise<void> {
		return new Promise((resolve, reject) => {
			user.changePassword(currentPassword, newPassword, error => {
				if (error) {
					return reject(error);
				}
				resolve();
			});
		});
	}

	confirmRegistration(user: CognitoUser, code: string): Promise<void> {
		this.networkActionsErrorsManager.clearError();
		return new Promise((resolve, reject) => {
			user.confirmRegistration(code, true, async error => {
				if (isNotNil(error)) {
					reject(error);
				} else {
					resolve();
				}
			});
		});
	}

	resendConfirmationCode(user: CognitoUser): Promise<void> {
		this.networkActionsErrorsManager.clearError();
		return new Promise((resolve, reject) => {
			user.resendConfirmationCode(error => {
				if (isNotNil(error)) {
					reject(error);
				} else {
					resolve();
				}
			});
		});
	}
}
